import { useState, type ReactNode } from 'react';

export interface TableView {
  headers: string[];
  rows: (string | number)[][];
}

/**
 * Rahmen für jedes Diagramm der Seite „Daten".
 *
 * Die aufklappbare Tabelle ist nicht bloß Beiwerk: sie macht jeden Wert ohne
 * Maus und ohne Farbunterscheidung lesbar. Für Serienfarben, die den 3:1-
 * Kontrast gegen Weiß nicht erreichen, ist genau das die Auflage.
 */
export default function ChartCard({
  title,
  hint,
  children,
  table,
  right,
  dimmed,
  caption,
}: {
  title: string;
  hint?: ReactNode;
  children: ReactNode;
  table?: TableView;
  right?: ReactNode;
  /** Während eines Refetch: vorhandene Darstellung halten statt Skelett. */
  dimmed?: boolean;
  /** Ausschnitt, auf den die Tabelle begrenzt ist – sonst wäre nicht ersichtlich,
   *  dass sie nicht den ganzen Zeitraum zeigt. */
  caption?: string;
}) {
  const [showTable, setShowTable] = useState(false);

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-start justify-between gap-4 mb-1">
        <h3 className="text-sm font-semibold text-gray-700">{title}</h3>
        <div className="flex items-center gap-3 shrink-0">
          {right}
          {table && (
            <button
              onClick={() => setShowTable(v => !v)}
              className="text-xs text-blue-600 hover:underline"
              aria-expanded={showTable}
            >
              {showTable ? 'Diagramm' : 'Tabelle'}
            </button>
          )}
        </div>
      </div>
      {hint && <p className="text-xs text-gray-400 mb-3">{hint}</p>}

      <div className={dimmed ? 'opacity-50 transition-opacity' : 'transition-opacity'}>
        {showTable && table ? (
          <>
            {caption && (
              <p className="text-xs text-gray-400 mb-1.5">
                Ausschnitt: {caption} ({table.rows.length}{' '}
                {table.rows.length === 1 ? 'Tag' : 'Tage'})
              </p>
            )}
            <DataTable table={table} />
          </>
        ) : (
          children
        )}
      </div>
    </div>
  );
}

function DataTable({ table }: { table: TableView }) {
  return (
    <div className="overflow-x-auto max-h-80 overflow-y-auto">
      <table className="w-full text-sm">
        <thead className="sticky top-0 bg-white">
          <tr className="text-left text-xs text-gray-500 border-b">
            {table.headers.map(header => (
              <th key={header} className="py-1.5 pr-4 font-medium whitespace-nowrap">
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {table.rows.map((row, index) => (
            <tr key={index} className="border-b border-gray-50">
              {row.map((cell, cellIndex) => (
                <td
                  key={cellIndex}
                  className={`py-1.5 pr-4 whitespace-nowrap ${
                    cellIndex === 0 ? 'text-gray-700' : 'text-gray-500 tabular-nums'
                  }`}
                >
                  {cell}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
